import React from 'react';
import {
  Box,
  Container,
  Flex,
  HStack,
  VStack,
  Text,
  IconButton,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import CompanyBadge from '../subComponents/CompanyBadge';

interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Optional subtitle shown below the title */
  subtitle?: string;
  /** Emoji icon shown in the gradient icon box */
  icon: string;
  /** Callback for the back button. If omitted, no back button is rendered. */
  onBack?: () => void;
  /** Chakra bgGradient for the icon box (default: blue→purple) */
  iconGradient?: string;
  /** Chakra bgGradient for the title text (default: blue→purple) */
  titleGradient?: string;
  /** Optional content rendered on the right side of the header row */
  rightContent?: React.ReactNode;
}

/**
 * Consistent frosted-glass page header used across all sub-pages.
 * Renders: back button (optional) | icon + title + subtitle | right content (optional)
 * Below: centered CompanyBadge from AuthContext.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  onBack,
  iconGradient = 'linear(135deg, blue.400, purple.500)',
  titleGradient = 'linear(135deg, blue.600, purple.600)',
  rightContent,
}) => {
  const { selectedCompany } = useAuth();

  return (
    <Box
      bg="rgba(255, 255, 255, 0.9)"
      backdropFilter="blur(20px)"
      borderBottom="1px"
      borderColor="whiteAlpha.300"
      px={4}
      py={6}
      w="100%"
      boxShadow="0 4px 32px rgba(0,0,0,0.08)"
    >
      <Container maxW="6xl" w="100%">
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            {onBack && (
              <IconButton
                aria-label="Go back"
                icon={<FiArrowLeft />}
                onClick={onBack}
                variant="ghost"
                size="lg"
                borderRadius="xl"
                _hover={{ bg: 'blue.50', transform: 'translateY(-1px)' }}
                transition="all 0.2s"
              />
            )}
            <HStack spacing={3}>
              <Box
                p={2}
                borderRadius="lg"
                bgGradient={iconGradient}
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 4px 16px rgba(59, 130, 246, 0.3)"
              >
                <Text fontSize="xl" color="white">{icon}</Text>
              </Box>
              <VStack align="start" spacing={0}>
                <Text
                  fontSize="2xl"
                  fontWeight="800"
                  bgGradient={titleGradient}
                  bgClip="text"
                >
                  {title}
                </Text>
                {subtitle && (
                  <Text fontSize="sm" color="gray.500">
                    {subtitle}
                  </Text>
                )}
              </VStack>
            </HStack>
          </HStack>
          {rightContent}
        </Flex>
        <Box display="flex" justifyContent="center" mt={4}>
          <CompanyBadge
            companyName={selectedCompany?.companyName || ''}
            companyLogo={selectedCompany?.companyLogo || ''}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default PageHeader;
